(() => {
  // theme/donation-quantity-lock/quantity-lock.js
  var DONATION_PRODUCT_TAG = "custom-price-donation";
  var MAX_QUANTITY_ONE_PRODUCT_TAG = "max-quantity-1";
  var CUSTOM_PRICE_PROPERTY = "_custom_price";
  function isDonationProduct(item) {
    const tags = item?.tags;
    return Array.isArray(tags) && tags.includes(DONATION_PRODUCT_TAG);
  }
  function isMaxQuantityOneProduct(item) {
    const tags = item?.tags;
    return Array.isArray(tags) && tags.includes(MAX_QUANTITY_ONE_PRODUCT_TAG);
  }
  function isQuantityOneProduct(item) {
    return isDonationProduct(item) || isMaxQuantityOneProduct(item);
  }
  function findQuantityOneLinesToClamp(cartItems) {
    if (!Array.isArray(cartItems)) {
      return [];
    }
    return cartItems.filter(
      (item) => isQuantityOneProduct(item) && item.quantity !== 1
    );
  }
  function normalizeQuantityFields(form) {
    const quantityInputs = form.querySelectorAll('input[name="quantity"]');
    if (quantityInputs.length === 0) {
      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = "quantity";
      hidden.value = "1";
      form.appendChild(hidden);
      return;
    }
    quantityInputs.forEach((input) => {
      input.value = "1";
    });
  }
  function hasCustomPriceProperty(properties) {
    if (!properties || typeof properties !== "object") {
      return false;
    }
    return CUSTOM_PRICE_PROPERTY in properties;
  }
  function isQuantityOneFormDataCartAdd(body, quantityOneVariantIds) {
    const variantId = body.get("id");
    if (variantId && quantityOneVariantIds.has(String(variantId))) {
      return true;
    }
    for (const [key] of body.entries()) {
      if (key.includes(CUSTOM_PRICE_PROPERTY)) {
        return true;
      }
    }
    return false;
  }
  function isQuantityOneCartAddItem(item, quantityOneVariantIds) {
    if (item?.id != null && quantityOneVariantIds.has(String(item.id))) {
      return true;
    }
    return hasCustomPriceProperty(item?.properties);
  }
  function shouldForceQuantityOneInCartAdd(body, quantityOneVariantIds) {
    if (!body) {
      return false;
    }
    if (body instanceof FormData) {
      return isQuantityOneFormDataCartAdd(body, quantityOneVariantIds);
    }
    if (typeof body === "string") {
      try {
        const payload = JSON.parse(body);
        if (!Array.isArray(payload.items) || payload.items.length === 0) {
          return false;
        }
        return payload.items.some(
          (item) => isQuantityOneCartAddItem(item, quantityOneVariantIds)
        );
      } catch {
        return false;
      }
    }
    return false;
  }
  function forceQuantityOneInFormData(body) {
    body.set("quantity", "1");
  }
  function forceQuantityOneInJsonCartAdd(body, quantityOneVariantIds) {
    const payload = JSON.parse(body);
    if (!Array.isArray(payload.items)) {
      return body;
    }
    payload.items = payload.items.map(
      (item) => isQuantityOneCartAddItem(item, quantityOneVariantIds) ? { ...item, quantity: 1 } : item
    );
    return JSON.stringify(payload);
  }

  // theme/donation-quantity-lock/init.js
  var QUANTITY_ONE_FORM_SELECTOR = 'form[data-type="add-to-cart-form"]:has(quantity-one-product)';
  var clampInFlight = false;
  function collectQuantityOneVariantIdsFromPage() {
    const ids = /* @__PURE__ */ new Set();
    document.querySelectorAll(QUANTITY_ONE_FORM_SELECTOR).forEach((form) => {
      form.querySelectorAll('input[name="id"]').forEach((input) => {
        if (input.value) {
          ids.add(String(input.value));
        }
      });
    });
    return ids;
  }
  function patchCartAddBody(input, init) {
    if (typeof input !== "string" || !input.includes("/cart/add")) {
      return init;
    }
    if (!init?.body) {
      return init;
    }
    const quantityOneVariantIds = collectQuantityOneVariantIdsFromPage();
    if (!shouldForceQuantityOneInCartAdd(init.body, quantityOneVariantIds)) {
      return init;
    }
    if (init.body instanceof FormData) {
      forceQuantityOneInFormData(init.body);
      return init;
    }
    if (typeof init.body === "string") {
      try {
        return {
          ...init,
          body: forceQuantityOneInJsonCartAdd(init.body, quantityOneVariantIds)
        };
      } catch {
        return init;
      }
    }
    return init;
  }
  function bindQuantityOneForm(form) {
    form.addEventListener(
      "submit",
      () => {
        normalizeQuantityFields(form);
      },
      true
    );
  }
  async function enrichCartItemsWithProductTags(items) {
    if (!Array.isArray(items) || items.length === 0) {
      return items ?? [];
    }
    const handles = [
      ...new Set(items.map((item) => item.handle).filter(Boolean))
    ];
    const tagsByHandle = /* @__PURE__ */ new Map();
    await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) {
            return void 0;
          }
          const product = await response.json();
          tagsByHandle.set(handle, product.tags ?? []);
          return void 0;
        } catch {
          return void 0;
        }
      })
    );
    return items.map((item) => ({
      ...item,
      tags: tagsByHandle.get(item.handle ?? "") ?? item.tags ?? []
    }));
  }
  async function clampQuantityOneCartLines(cart) {
    const enrichedItems = await enrichCartItemsWithProductTags(cart.items);
    const lines = findQuantityOneLinesToClamp(enrichedItems);
    if (lines.length === 0) {
      return cart;
    }
    let latestCart = cart;
    for (const line of lines) {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ id: line.key, quantity: 1 })
      });
      latestCart = await response.json();
    }
    return latestCart;
  }
  async function clampCartIfNeeded() {
    if (clampInFlight) {
      return void 0;
    }
    clampInFlight = true;
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();
      return await clampQuantityOneCartLines(cart);
    } finally {
      clampInFlight = false;
    }
  }
  function installFetchQuantityGuard() {
    const nativeFetch = window.fetch.bind(window);
    window.fetch = (input, init) => {
      const nextInit = patchCartAddBody(input, init ? { ...init } : init);
      return nativeFetch(input, nextInit);
    };
  }
  function bindCartEvents() {
    if (typeof theme === "undefined" || !theme.pubsub) {
      return;
    }
    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
      clampCartIfNeeded();
    });
    document.addEventListener("cart:updated", () => {
      clampCartIfNeeded();
    });
  }
  function initDonationQuantityLock() {
    document.querySelectorAll(QUANTITY_ONE_FORM_SELECTOR).forEach(bindQuantityOneForm);
    installFetchQuantityGuard();
    bindCartEvents();
    clampCartIfNeeded();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initDonationQuantityLock);
  } else {
    initDonationQuantityLock();
  }
})();
